import { useMemo, useState, type ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useMenuItems, type MenuItemRow, type PriceRow } from "@/hooks/useMenuItems";
import { usePackages, type PackageRow } from "@/hooks/usePackages";
import { useDietaryTags, getDietaryTagLabel } from "@/hooks/useDietaryTags";
import type { PickedItem } from "@/components/quotes/QuoteItemPicker";
import { groupItemsByCategory } from "@/lib/quote-item-categories";
import { lineTotal } from "@/lib/quote-totals";
import { cn } from "@/lib/utils";
import { formatMoney } from "./drawerLogic";
import { DietaryTags } from "./ItemEditRow";

interface ItemAddBarProps {
  guests: number;
  /** Lifted to the drawer, so Esc can clear it before it closes anything. */
  search: string;
  onSearchChange: (value: string) => void;
  onPick: (item: PickedItem) => void;
  onCustom: (name: string) => void;
  /** Load template, beside the search box. */
  children?: ReactNode;
}

const PACKAGES = "__packages__";
const ALL = "__all__";
const UNIT_SUFFIX: Record<string, string> = { per_person: " / guest", flat: "" };

const matches = (q: string, ...texts: (string | null | undefined)[]) => !q || texts.some((t) => t?.toLowerCase().includes(q));

// Picked lines follow QuoteItemPicker's rules: per-person quantity is the guest count.
function pickMenuItem(item: MenuItemRow, price: PriceRow, guests: number, sized: boolean): PickedItem {
  const quantity = price.pricing_type === "per_person" ? guests : 1;
  const description = sized ? [price.label, item.description].filter(Boolean).join(". ") : item.description ?? "";
  return {
    name: item.name, description, pricing_type: price.pricing_type, unit_price: price.price,
    quantity, line_total: lineTotal(price.price, quantity), menu_item_id: item.id,
  };
}

function pickPackage(pkg: PackageRow, guests: number): PickedItem {
  const perPerson = pkg.pricing_type === "per_person";
  const quantity = perPerson ? guests : 1;
  return {
    name: pkg.name, description: pkg.description ?? "", pricing_type: perPerson ? "per_person" : "custom",
    unit_price: pkg.price, quantity, line_total: lineTotal(pkg.price, quantity), package_id: pkg.id,
  };
}

const rowClass = "grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 rounded-md px-2 py-1.5 text-left text-[13.5px] hover:bg-muted";

function MenuItemResult({ item, tags, guests, onPick }: { item: MenuItemRow; tags: string[]; guests: number; onPick: (p: PickedItem) => void }) {
  const [sizesOpen, setSizesOpen] = useState(false);
  const prices = item.prices ?? [];
  const sized = prices.length > 1;
  const priceText = prices.length === 0
    ? "No prices configured"
    : sized
      ? `${prices.length} sizes from ${formatMoney(Math.min(...prices.map((p) => p.price)))}`
      : `${formatMoney(prices[0].price)}${UNIT_SUFFIX[prices[0].pricing_type] ?? " each"}`;
  return (
    <>
      <CommandItem
        value={`item-${item.id}`}
        disabled={prices.length === 0}
        onSelect={() => (sized ? setSizesOpen((o) => !o) : onPick(pickMenuItem(item, prices[0], guests, false)))}
        className={rowClass}
      >
        <span className="min-w-0">{item.name}<DietaryTags tags={tags} /></span>
        <small className="text-muted-foreground tabular-nums">{priceText}</small>
      </CommandItem>
      {sized && sizesOpen && (
        <div className="flex flex-wrap gap-1.5 px-2 pb-2 pt-0.5">
          {prices.map((p) => (
            <CommandItem
              key={p.id}
              value={`size-${p.id}`}
              onSelect={() => onPick(pickMenuItem(item, p, guests, true))}
              className="rounded-md border border-accent bg-accent-soft px-2 py-1 text-[12.5px]"
            >
              {p.label || p.pricing_type} · {formatMoney(p.price)}
            </CommandItem>
          ))}
        </div>
      )}
    </>
  );
}

function PackageResult({ pkg, guests, onPick }: { pkg: PackageRow; guests: number; onPick: (p: PickedItem) => void }) {
  const range = pkg.min_guests ? `${pkg.min_guests}–${pkg.max_guests ?? "∞"} guests` : null;
  return (
    <CommandItem value={`pkg-${pkg.id}`} onSelect={() => onPick(pickPackage(pkg, guests))} className={rowClass}>
      <span className="min-w-0">
        {pkg.name}
        {range && <small className="block text-muted-foreground">{range}</small>}
      </span>
      <small className="text-muted-foreground tabular-nums">
        {formatMoney(pkg.price)}{pkg.pricing_type === "per_person" ? " / guest" : ""}
      </small>
    </CommandItem>
  );
}

/**
 * The add bar under the edit rows: search or browse the catalog, packages and
 * "+ Custom item" (#1559). The results are a cmdk list (filtered here, so
 * shouldFilter is off), so arrows move through them from the search box and
 * Enter picks one instead of submitting the edit form, as in QuoteItemPicker.
 */
export function ItemAddBar({ guests, search, onSearchChange, onPick, onCustom, children }: ItemAddBarProps) {
  const { data: menuItems } = useMenuItems();
  const { data: packages } = usePackages();
  const { data: dietary } = useDietaryTags();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(ALL);
  const q = search.trim().toLowerCase();

  const groups = useMemo(() => groupItemsByCategory((menuItems ?? []).filter((i) => i.is_active)), [menuItems]);
  const activePackages = (packages ?? []).filter((p) => p.is_active);
  const items = groups
    .filter((g) => category === ALL || g.id === category)
    .flatMap((g) => g.items)
    .filter((i) => matches(q, i.name, i.description));
  const pkgs = category === ALL || category === PACKAGES ? activePackages.filter((p) => matches(q, p.name, p.description)) : [];
  const chips = [{ id: ALL, name: "All" }, ...(activePackages.length ? [{ id: PACKAGES, name: "Packages" }] : []), ...groups];

  const done = (fn: () => void) => {
    fn();
    onSearchChange("");
    setOpen(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-b-[10px] border-t border-dashed bg-background px-3 py-2.5">
      <Popover open={open} onOpenChange={setOpen}>
        <Command shouldFilter={false} label="Search catalog" className="min-w-0 flex-[1_1_240px] overflow-visible rounded-none bg-transparent">
          <PopoverPrimitive.Anchor asChild>
            <div className="rounded-md border border-input bg-background [&_[cmdk-input-wrapper]]:border-b-0 [&_[cmdk-input-wrapper]]:px-2.5">
              <CommandInput
                aria-label="Search catalog"
                placeholder="Add an item: search or browse the catalog…"
                autoComplete="off"
                value={search}
                onFocus={() => setOpen(true)}
                onValueChange={(value) => {
                  onSearchChange(value);
                  if (value) setCategory(ALL);
                  setOpen(true);
                }}
                className="h-9 py-0 text-base sm:text-sm"
              />
            </div>
          </PopoverPrimitive.Anchor>
          {/* cmdk's input always says it controls the list, so an empty one stands in while the results are closed. */}
          {!open && <CommandList hidden />}
          <PopoverContent
            align="start"
            className="max-h-[340px] w-[var(--radix-popover-trigger-width)] min-w-[280px] overflow-auto p-1.5"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              // Clicking back in the search box keeps the results open.
              if ((e.target as HTMLElement | null)?.closest?.("[aria-label='Search catalog']")) e.preventDefault();
            }}
          >
            {/* Enter on a chip toggles it, rather than reaching cmdk's pick-the-selected-result.
                z-10: cmdk rows are position:relative, so without it they paint over this sticky bar.
                One scrolling row on phones, so the chips don't take most of the list's height. */}
            <div role="group" aria-label="Filter by category" onKeyDown={(e) => { if (e.key === "Enter") e.stopPropagation(); }} className="sticky -top-1.5 z-10 mb-1 flex gap-1.5 overflow-x-auto border-b bg-popover px-1 pb-2 pt-1 sm:flex-wrap">
              {chips.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={category === c.id}
                  onClick={() => setCategory(c.id)}
                  className={cn(
                    "shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[12px] font-semibold",
                    category === c.id ? "border-foreground bg-foreground text-background" : "bg-background",
                  )}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <CommandList className="max-h-none overflow-visible">
              <div className="grid gap-0.5">
                {category !== PACKAGES && items.map((item) => (
                  <MenuItemResult
                    key={item.id}
                    item={item}
                    guests={guests}
                    tags={(item.dietary_tags ?? []).map((t) => getDietaryTagLabel(dietary, t))}
                    onPick={(p) => done(() => onPick(p))}
                  />
                ))}
                {pkgs.map((pkg) => (
                  <PackageResult key={pkg.id} pkg={pkg} guests={guests} onPick={(p) => done(() => onPick(p))} />
                ))}
                {items.length === 0 && pkgs.length === 0 && (
                  <div className="px-2 py-1.5 text-[12.5px] text-muted-foreground">Nothing in the catalog matches.</div>
                )}
                {search.trim() && (
                  <CommandItem value="__custom__" onSelect={() => done(() => onCustom(search.trim()))} className={rowClass}>
                    <span>+ Custom item "{search.trim()}"</span>
                    <small className="text-muted-foreground">not in the catalog</small>
                  </CommandItem>
                )}
              </div>
            </CommandList>
          </PopoverContent>
        </Command>
      </Popover>
      {children}
    </div>
  );
}
